import React, { useState, useCallback } from 'react';
import { Plus, Minus } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import LazyImage from './LazyImage';

const ProductCard = React.memo(({ product }) => {
  const [quantity, setQuantity] = useState(0);
  const [unit, setUnit] = useState('PCS');
  const { addToCart } = useCart();

  const handleAddToCart = useCallback(() => {
    if (quantity < 1) { toast.error('Please select quantity'); return; }
    addToCart(product, quantity, unit);
    toast.success(`Added ${quantity} ${unit} of ${product.name}`);
    setQuantity(0);
  }, [product, quantity, unit, addToCart]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3 flex gap-3" data-testid={`product-card-${product.id}`}>
      {/* Image */}
      <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-50 relative">
        <LazyImage src={product.image_url} alt={product.name} className="w-full h-full object-contain" />
        {!product.in_stock && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="text-white text-[8px] font-bold bg-red-600 px-1.5 py-0.5 rounded">OUT</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Name + Stock */}
        <div className="flex items-start justify-between gap-1 mb-1">
          <h3 className="text-sm font-semibold text-[#1A3626] leading-tight line-clamp-2" data-testid={`product-name-${product.id}`}>
            {product.name}
          </h3>
          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 ${product.in_stock ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`} data-testid={`stock-status-${product.id}`}>
            {product.in_stock ? 'In Stock' : 'Out of Stock'}
          </span>
        </div>

        {/* Price */}
        <p className="text-xs text-gray-500 mb-2" data-testid={`product-price-${product.id}`}>
          AMT <span className="font-bold text-[#1A3626]">₹{product.price.toFixed(2)}</span>
        </p>

        {/* Controls row */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Unit */}
          <select
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            className="bg-gray-100 border border-gray-200 rounded-md px-2 py-1.5 text-xs font-medium text-[#1A3626] focus:outline-none focus:border-[#1A3626]"
            data-testid={`unit-selector-${product.id}`}
          >
            {product.units?.map(u => <option key={u} value={u}>{u}</option>)}
          </select>

          {/* Qty */}
          <div className="flex items-center border border-gray-200 rounded-md overflow-hidden">
            <button onClick={() => setQuantity(Math.max(0, quantity - 1))} className="px-2 py-1.5 bg-gray-100 hover:bg-gray-200 text-[#1A3626]" data-testid={`quantity-decrease-${product.id}`}>
              <Minus className="h-3 w-3" />
            </button>
            <input type="number" value={quantity} onChange={(e) => setQuantity(Math.max(0, parseInt(e.target.value) || 0))}
              className="w-10 text-center text-xs font-semibold border-x border-gray-200 py-1.5 focus:outline-none" min="1" data-testid={`quantity-input-${product.id}`} />
            <button onClick={() => setQuantity(quantity + 1)} className="px-2 py-1.5 bg-gray-100 hover:bg-gray-200 text-[#1A3626]" data-testid={`quantity-increase-${product.id}`}>
              <Plus className="h-3 w-3" />
            </button>
          </div>

          {/* ADD button */}
          <button onClick={handleAddToCart} disabled={!product.in_stock}
            className="px-4 py-1.5 bg-[#1A3626] text-white text-xs font-semibold rounded-md hover:bg-[#2a5440] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            data-testid={`add-to-cart-${product.id}`}>
            ADD
          </button>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between mt-2">
          <p className="text-[9px] text-gray-400">Serviced by <span className="font-medium text-gray-500">Krishna Machinery Stores</span></p>
          <p className="text-[10px] text-gray-500">Total Qty <span className="font-bold text-[#1A3626]">{quantity}</span></p>
        </div>
      </div>
    </div>
  );
});

ProductCard.displayName = 'ProductCard';

export default ProductCard;
